import React, { useEffect, useState, useRef } from 'react';
import { leadActivitiesAPI } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { MessageCircle, Send, ArrowDown, Bot, Globe, RefreshCw, Clock, CheckCircle, XCircle, Loader2, Zap, Sparkles, Reply, AlertTriangle, Check, CheckCheck, FileText } from 'lucide-react';
import { formatLocal } from '../lib/dateUtils';
import { toast } from 'sonner';

const STATUS_STYLES = {
  completed: { icon: CheckCircle, color: 'text-emerald-500', label: 'Sent' },
  pending: { icon: Clock, color: 'text-amber-500', label: 'Pending' },
  queued: { icon: Clock, color: 'text-blue-500', label: 'Queued' },
  failed: { icon: XCircle, color: 'text-red-500', label: 'Failed' },
  cancelled: { icon: XCircle, color: 'text-zinc-500', label: 'Cancelled' },
  cancelled_opt_out: { icon: XCircle, color: 'text-zinc-500', label: 'Opt-out' },
};

const SENTIMENT_COLORS = {
  positive: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  neutral: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
  negative: 'bg-red-500/15 text-red-400 border-red-500/30',
};

// Real WhatsApp delivery lifecycle (from Meta webhook status events).
function friendlyDeliveryError(code, reason) {
  if (code === 131049 || code === '131049')
    return "Not delivered — Meta withheld this marketing message (131049). Send a UTILITY template or wait for the lead to reply.";
  if (code === 131047 || code === '131047')
    return "Not delivered — 24h window closed (131047). Send an approved template to re-open it.";
  if (code === 131026 || code === '131026')
    return "Not delivered — recipient number is not on WhatsApp (131026).";
  if (code === 470 || code === '470')
    return "Not delivered — outside the 24h customer-care window (470).";
  return reason ? `Not delivered — ${reason}${code ? ` (${code})` : ''}` : 'Message failed to deliver';
}

function DeliveryTick({ status, code, reason }) {
  if (status === 'read') return <CheckCheck className="h-3 w-3 text-sky-300" title="Read" />;
  if (status === 'delivered') return <CheckCheck className="h-3 w-3 text-white/70" title="Delivered" />;
  if (status === 'failed') return <AlertTriangle className="h-3 w-3 text-red-300" title={friendlyDeliveryError(code, reason)} />;
  return <Check className="h-3 w-3 text-white/60" title="Sent (accepted by Meta — awaiting delivery)" />;
}

function OutboundBubble({ msg }) {
  const status = STATUS_STYLES[msg.status] || STATUS_STYLES.pending;
  const StatusIcon = status.icon;
  const ds = msg.delivery_status;
  const showDelivery = ds && msg.status !== 'draft_pending';
  const rawMsg = typeof msg.message === 'string' ? msg.message : '';
  const templateName = msg.template_name
    || (rawMsg.startsWith('[template:') && rawMsg.includes(']') ? rawMsg.slice('[template:'.length, rawMsg.indexOf(']')) : null);
  const displayText = msg.template_body
    ? msg.template_body
    : (templateName ? (rawMsg.replace(/^\[template:[^\]]*\]\s*/, '').trim() || 'Template message sent') : rawMsg);
  return (
    <div className="flex justify-end mb-3" data-testid={`wa-outbound-${msg.id}`}>
      <div className="max-w-[75%] space-y-1">
        <div className="bg-[#005c4b] text-white rounded-xl rounded-tr-sm px-4 py-2.5 shadow-sm">
          {templateName && (
            <div className="flex items-center gap-1 mb-1 text-[10px] text-emerald-200/90 uppercase tracking-wide"
                 data-testid={`wa-template-badge-${msg.id}`}>
              <FileText className="h-2.5 w-2.5" /> Template · {templateName}
            </div>
          )}
          <p className="text-[13px] leading-relaxed whitespace-pre-wrap" data-testid={`wa-outbound-text-${msg.id}`}>{displayText}</p>
          {showDelivery && ds === 'failed' && (
            <div className="mt-1.5 flex items-start gap-1 text-[10px] text-red-100 bg-red-500/25 border border-red-300/40 rounded px-1.5 py-1"
                 data-testid={`wa-thread-delivery-error-${msg.id}`}>
              <AlertTriangle className="h-2.5 w-2.5 mt-0.5 flex-shrink-0" />
              <span>{friendlyDeliveryError(msg.delivery_error_code, msg.delivery_error_reason)}</span>
            </div>
          )}
          <div className="flex items-center justify-end gap-1.5 mt-1.5">
            {msg.auto_triggered && (
              <Zap className="h-3 w-3 text-amber-300" />
            )}
            {msg.language && msg.language !== 'en' && (
              <span className="text-[10px] opacity-70 uppercase">{msg.language}</span>
            )}
            <span className="text-[10px] opacity-60">{msg.timestamp ? formatLocal(msg.timestamp) : ''}</span>
            {showDelivery
              ? <DeliveryTick status={ds} code={msg.delivery_error_code} reason={msg.delivery_error_reason} />
              : <StatusIcon className={`h-3 w-3 ${msg.status === 'completed' ? 'text-sky-300' : 'opacity-50'}`} />}
          </div>
        </div>
        <div className="flex items-center justify-end gap-1.5 px-1">
          {msg.auto_triggered && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-amber-500/40 text-amber-500">
              <Zap className="h-2.5 w-2.5 mr-0.5" /> Auto: {(msg.trigger_event || '').replace(/_/g, ' ')}
            </Badge>
          )}
          {msg.sequence_step && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-blue-500/40 text-blue-400">
              Step {msg.sequence_step}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

function InboundBubble({ msg }) {
  const analysis = msg.brain_analysis;
  const sentimentClass = analysis ? (SENTIMENT_COLORS[analysis.sentiment] || SENTIMENT_COLORS.neutral) : '';
  return (
    <div className="flex justify-start mb-3" data-testid={`wa-inbound-${msg.id}`}>
      <div className="max-w-[75%] space-y-1">
        <div className="bg-[#1f2c33] text-zinc-100 rounded-xl rounded-tl-sm px-4 py-2.5 shadow-sm border border-zinc-700/50">
          <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{msg.message}</p>
          <div className="flex items-center gap-1.5 mt-1.5">
            {msg.detected_language && (
              <span className="text-[10px] opacity-70 uppercase">{msg.detected_language}</span>
            )}
            <span className="text-[10px] opacity-50">{msg.timestamp ? formatLocal(msg.timestamp) : ''}</span>
          </div>
        </div>
        {analysis && (
          <div className="flex items-center gap-1.5 px-1 flex-wrap">
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 ${sentimentClass}`}>
              {analysis.sentiment}
            </Badge>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-zinc-600 text-zinc-400">
              <Bot className="h-2.5 w-2.5 mr-0.5" /> {analysis.intent?.replace(/_/g, ' ')}
            </Badge>
            {analysis.suggested_action && analysis.suggested_action !== 'no_action' && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-violet-500/40 text-violet-400">
                Suggest: {analysis.suggested_action.replace(/_/g, ' ')}
              </Badge>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function WhatsAppThreadView({ leadId, leadName, onSendMessage }) {
  const [thread, setThread] = useState([]);
  const [stats, setStats] = useState({ outbound_count: 0, inbound_count: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [quickReplyLoading, setQuickReplyLoading] = useState(false);
  const [sendingReply, setSendingReply] = useState(null);
  const [windowInfo, setWindowInfo] = useState(null);
  const [sessionText, setSessionText] = useState('');
  const [sendingSession, setSendingSession] = useState(false);
  const [humanNeeded, setHumanNeeded] = useState(null);
  const [resumingAI, setResumingAI] = useState(false);
  const scrollRef = useRef(null);

  const loadThread = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await leadActivitiesAPI.getWhatsAppConversation(leadId);
      const data = res.data || res;
      setThread(data.thread || []);
      setStats({ outbound_count: data.outbound_count || 0, inbound_count: data.inbound_count || 0 });
      setWindowInfo(data.window || null);
      setHumanNeeded(data.human_needed || null);
      
      // Auto-load quick replies if last message is inbound
      const threadData = data.thread || [];
      if (threadData.length > 0 && threadData[threadData.length - 1].direction === 'inbound') {
        loadQuickReplies();
      } else {
        setQuickReplies([]);
      }
    } catch {
      setThread([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleResumeAI = async () => {
    setResumingAI(true);
    try {
      await leadActivitiesAPI.resumeWhatsAppAI(leadId);
      setHumanNeeded(null);
      toast.success('AI resumed — it will reply to new messages on this chat');
      loadThread(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resume AI');
    } finally {
      setResumingAI(false);
    }
  };

  const loadQuickReplies = async () => {
    setQuickReplyLoading(true);
    try {
      const res = await leadActivitiesAPI.getWhatsAppQuickReplies(leadId);
      const data = res.data || res;
      setQuickReplies(data.replies || []);
    } catch {
      setQuickReplies([]);
    } finally {
      setQuickReplyLoading(false);
    }
  };

  const handleQuickReply = async (reply) => {
    setSendingReply(reply.text);
    try {
      const res = await leadActivitiesAPI.sendWhatsApp(leadId, { message: reply.text });
      if (res?.data?.requires_template) {
        toast.message('Outside the 24h window', { description: 'Open the WhatsApp composer to send an approved template.' });
        return;
      }
      toast.success('Reply sent');
      setQuickReplies([]);
      loadThread(true);
    } catch (err) {
      toast.error('Failed to send reply', { description: err?.response?.data?.detail || 'Try again' });
    } finally {
      setSendingReply(null);
    }
  };

  // Free-text session reply — only allowed while Meta's 24h window is OPEN.
  const handleSendSession = async () => {
    const msg = sessionText.trim();
    if (!msg) return;
    setSendingSession(true);
    try {
      const res = await leadActivitiesAPI.sendWhatsApp(leadId, { message: msg });
      if (res?.data?.requires_template) {
        toast.message('Outside the 24h window', { description: 'Free text can’t be delivered — send an approved template from the WhatsApp composer.' });
        return;
      }
      toast.success('Message sent');
      setSessionText('');
      loadThread(true);
    } catch (err) {
      toast.error('Failed to send', { description: err?.response?.data?.detail || 'Try again' });
    } finally {
      setSendingSession(false);
    }
  };

  useEffect(() => {
    if (leadId) loadThread();
  }, [leadId]);  // Scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current && thread.length > 0) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="whatsapp-thread-view">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-card rounded-t-lg">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-emerald-600/20 flex items-center justify-center">
            <MessageCircle className="h-4 w-4 text-emerald-500" />
          </div>
          <div>
            <p className="text-sm font-medium" data-testid="wa-thread-lead-name">{leadName || 'WhatsApp'}</p>
            <p className="text-[11px] text-muted-foreground">
              {stats.outbound_count} sent, {stats.inbound_count} received
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost" size="sm"
            onClick={() => loadThread(true)}
            disabled={refreshing}
            data-testid="wa-thread-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          {onSendMessage && (
            <Button
              size="sm" variant="default"
              onClick={onSendMessage}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="wa-thread-send-btn"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" /> Send Message
            </Button>
          )}
        </div>
      </div>

      {/* Phase 6 · Human handoff banner — AI is paused; a rep owns this thread */}
      {humanNeeded && (
        <div className="flex items-center justify-between gap-3 px-4 py-2 bg-amber-500/10 border-b border-amber-500/30"
             data-testid="wa-handoff-banner">
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
            <span className="text-[12px] text-amber-600 dark:text-amber-300 truncate">
              AI paused — a human is handling this chat{humanNeeded.reason ? ` (${humanNeeded.reason.replace(/_/g, ' ')})` : ''}.
            </span>
          </div>
          <Button
            size="sm" variant="outline"
            className="h-7 border-amber-500/50 text-amber-600 dark:text-amber-300 hover:bg-amber-500/15 shrink-0"
            disabled={resumingAI}
            onClick={handleResumeAI}
            data-testid="wa-resume-ai-btn"
          >
            {resumingAI ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Bot className="h-3 w-3 mr-1" />}
            Resume AI
          </Button>
        </div>
      )}

      {thread.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-600/10 flex items-center justify-center mb-4">
            <MessageCircle className="h-7 w-7 text-emerald-600/50" />
          </div>
          <p className="text-muted-foreground text-sm">No WhatsApp conversation yet</p>
          <p className="text-muted-foreground/60 text-xs mt-1">Send a message to start the thread</p>
          {onSendMessage && (
            <Button size="sm" className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={onSendMessage}>
              <Send className="h-3.5 w-3.5 mr-1.5" /> Start Conversation
            </Button>
          )}
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-4 space-y-1 max-h-[480px] bg-[#0b141a]/50"
          style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'6\' height=\'6\' viewBox=\'0 0 6 6\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.02\' fill-rule=\'evenodd\'%3E%3Cpath d=\'M5 0h1L0 6V5zM6 5v1H5z\'/%3E%3C/g%3E%3C/svg%3E")' }}
          data-testid="wa-thread-messages"
        >
          {thread.map((msg) =>
            msg.direction === 'outbound' ? (
              <OutboundBubble key={msg.id} msg={msg} />
            ) : (
              <InboundBubble key={msg.id} msg={msg} />
            )
          )}
        </div>
      )}

      {/* Quick Reply Suggestions */}
      {(quickReplies.length > 0 || quickReplyLoading) && (
        <div className="px-4 py-3 border-t border-border bg-card/80" data-testid="wa-quick-replies">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-3.5 w-3.5 text-amber-500" />
            <span className="text-xs font-medium text-muted-foreground">AI Quick Replies</span>
            <Button
              variant="ghost" size="sm"
              className="h-5 px-1.5 ml-auto"
              onClick={loadQuickReplies}
              disabled={quickReplyLoading}
              data-testid="wa-refresh-quick-replies"
            >
              <RefreshCw className={`h-3 w-3 ${quickReplyLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          {quickReplyLoading ? (
            <div className="flex gap-2">
              {[1,2,3].map(i => (
                <div key={i} className="h-9 bg-muted/50 rounded-full animate-pulse flex-1" />
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {quickReplies.map((reply, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuickReply(reply)}
                  disabled={sendingReply !== null}
                  className={`group flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium border transition-all duration-200 max-w-[280px] text-left
                    ${sendingReply === reply.text 
                      ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400' 
                      : 'border-border hover:border-emerald-500/50 hover:bg-emerald-500/10 text-foreground'
                    }
                  `}
                  data-testid={`wa-quick-reply-${idx}`}
                >
                  {sendingReply === reply.text ? (
                    <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
                  ) : (
                    <Reply className="h-3 w-3 flex-shrink-0 text-muted-foreground group-hover:text-emerald-500 transition-colors" />
                  )}
                  <span className="truncate">{reply.text}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Free-text composer — visible only while the 24h session window is OPEN
          (Meta blocks free-text outside it; a template is required to re-open). */}
      {windowInfo?.open && (
        <div className="px-4 py-3 border-t border-border bg-card/80" data-testid="wa-session-composer">
          <div className="flex items-end gap-2">
            <textarea
              rows={1}
              value={sessionText}
              onChange={(e) => setSessionText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendSession(); } }}
              placeholder="Type a message…"
              className="flex-1 resize-none rounded-lg bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 max-h-28"
              data-testid="wa-session-input"
            />
            <Button
              size="sm"
              className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
              onClick={handleSendSession}
              disabled={sendingSession || !sessionText.trim()}
              data-testid="wa-session-send"
            >
              {sendingSession ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
